import React, {
  ComponentProps,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  Grid,
  Modal,
  Paper,
  Stack,
  Typography,
  IconButton,
  CardMedia,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRowId,
  GridSelectionModel,
  itIT,
  GridRowModel,
  GridRenderCellParams,
  useGridApiContext,
} from "@mui/x-data-grid";
import { RHFTextField } from "../../../RHFComponents/RHFComponents";
import { useForm } from "react-hook-form";
import bluePencil from "../../../../assets/icons/Icon-bluePencil.svg";
import { DataContext } from "../Context/ContextWrapper";

/* Costanti & Tipi */

const COLORS = {
  navy: "#003267",
  white: "#FFFFFF",
  zebraEven: "#F8F9FC",
  zebraOdd: "#FFFFFF",
  border: "#CCCCCC",
  text: "#00000099",
};

const sectionTitleSx = { fontWeight: 700, color: COLORS.text, mb: 1 };

// Tipo delle righe della tabella
export type GroupableProcedureRow = {
  id: string;
  specialita: string;
  prestazione: string;
  codSiss: string;
  descrizioneSiss: string;
  qta: number;
  valDa: number | string;
  valAl: number | string;
  ntr: string;
};

// Props (solo procedures: i dati reali dall’API già filtrata)
type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: Record<string, string>) => void | Promise<void>;
  procedures: any[]; // formato grezzo API (array di RIGAGRID)
  aziendaPrenotazione: string;
  idPacada: string;
  groupingHeader: { tipo: string; descr: string };
};

type FormValues = {
  tipologiaRaggruppabili: string | null;
  tipologiaRaggruppamento: string | null;
  slotOccupati: string | null;
};

/*  Styled DataGrid */

function StyledDataGrid(props: ComponentProps<typeof DataGrid>) {
  return (
    <DataGrid
      {...props}
      sx={{
        border: `1px solid ${COLORS.border}`,
        width: "100%",
        borderRadius: 2,
        display: "flex",
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: COLORS.navy,
          color: COLORS.white,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
        "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
          color: COLORS.white,
        },
        "& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-sortIcon": {
          color: COLORS.white,
        },
        "& .MuiDataGrid-columnSeparator": { display: "none" },
        "& .MuiDataGrid-row:nth-of-type(even)": {
          backgroundColor: COLORS.zebraEven,
        },
        "& .MuiDataGrid-row:nth-of-type(odd)": {
          backgroundColor: COLORS.zebraOdd,
        },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
          outline: "none",
        },
        "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
          { outline: "none" },
        // Evidenziazione righe selezionate
        "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover":
          { backgroundColor: "#DFEAFF !important" },
        ...(props.sx || {}),
      }}
      headerHeight={36}
      rowHeight={40}
      hideFooter
      disableColumnFilter
      disableColumnSelector
      disableColumnMenu
      localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
    />
  );
}

/*  Cella matita (apre l’edit su Q.Tà)  */
const PenEditButton: FC<GridRenderCellParams> = (params) => {
  const apiRef = useGridApiContext();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    const field = "qta";
    const mode = apiRef.current.getCellMode(params.id, field);

    if (mode === "edit") {
      apiRef.current.setCellFocus(params.id, field);
      return;
    }

    try {
      apiRef.current.stopCellEditMode({
        id: params.id,
        field,
        ignoreModifications: false,
      });
    } catch {}

    apiRef.current.setCellFocus(params.id, field);

    // Attendo la chiusura di eventuali edit in corso, poi apro l’edit su Q.Tà
    setTimeout(() => {
      if (apiRef.current.getCellMode(params.id, field) === "view") {
        apiRef.current.startCellEditMode({ id: params.id, field });
      }
    }, 0);
  };

  return (
    <IconButton size="small" onClick={handleClick}>
      <CardMedia
        component="img"
        src={bluePencil}
        alt="Modifica"
        sx={{ width: 16, height: 16 }}
      />
    </IconButton>
  );
};

/*  Colonne del DataGrid (riusate in entrambe le tabelle) */
const createColumns = (): GridColDef[] => [
  { field: "specialita", headerName: "Specialità", flex: 0.6, sortable: false },
  {
    field: "prestazione",
    headerName: "Prestazione",
    flex: 0.7,
    sortable: false,
  },
  { field: "codSiss", headerName: "Cod. SISS", flex: 0.7, sortable: false },
  {
    field: "descrizioneSiss",
    headerName: "Descrizione SISS",
    flex: 1.6,
    sortable: false,
  },
  {
    field: "qta",
    headerName: "Q.Tà",
    type: "number",
    editable: true,
    flex: 0.4,
    align: "center",
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "__pen__",
    headerName: "",
    width: 44,
    sortable: false,
    filterable: false,
    align: "center",
    headerAlign: "center",
    disableColumnMenu: true,
    renderHeader: () => <span />,
    renderCell: (p) => <PenEditButton {...p} />,
  },
  {
    field: "valDa",
    headerName: "Val.Da",
    flex: 0.5,
    align: "center",
    headerAlign: "center",
    sortable: false,
  },
  {
    field: "valAl",
    headerName: "Val.Al",
    flex: 0.5,
    align: "center",
    headerAlign: "center",
    sortable: false,
  },
  { field: "ntr", headerName: "NTR", flex: 0.7, sortable: false },
];

/*  Componente */

const GroupableProcedures: FC<Props> = ({
  open,
  onClose,
  onConfirm,
  procedures,
  aziendaPrenotazione,
  idPacada,
  groupingHeader,
}) => {
  // Righe per la tabella (parto vuoto: niente mock)
  const [data, setData] = useState<GroupableProcedureRow[]>([]);
  // Id delle righe selezionate
  const [selection, setSelection] = useState<GridSelectionModel>([]);

  const methods = useForm<FormValues>({
    defaultValues: {
      tipologiaRaggruppabili: "",
      tipologiaRaggruppamento: "",
      slotOccupati: "",
    },
  });
  const { control, setValue, reset } = methods;

  const context = useContext(DataContext);

  // Quando cambia "procedures", trasformo i dati grezzi API nelle righe DataGrid
  useEffect(() => {
    if (!Array.isArray(procedures) || procedures.length === 0) {
      setData([]);
      setSelection([]);
      return;
    }

    // Tengo solo le righe con dati reali (titolo === "false")
    const rowsWithData = procedures.filter(
      (row: any) => row?.titolo === "false" && Array.isArray(row?.dati)
    );

    // helper per leggere un "campo" specifico
    const read = (row: any, fieldName: string): string => {
      const cell = row.dati.find((c: any) => c?.campo === fieldName);
      return (cell?.dato ?? "").toString().trim();
    };

    const toNumber = (v: string) => {
      const n = Number((v || "").replace(",", "."));
      return Number.isFinite(n) ? Math.max(0, n) : 0;
    };

    const mapped: GroupableProcedureRow[] = rowsWithData.map(
      (row: any, idx: number) => ({
        id: row.pkey || String(idx + 1),
        specialita: read(row, "Spc."),
        prestazione: read(row, "Prest."),
        codSiss: read(row, "Cod. SISS"),
        descrizioneSiss: read(row, "Descr."),
        qta: toNumber(read(row, "Q.tà")),
        valDa: read(row, "Val.da") || "",
        valAl: read(row, "Val.a") || "",
        ntr: read(row, "NTR"),
      })
    );

    setData(mapped);
    setSelection([]); // reset selezione ad ogni refresh
  }, [procedures]);

  // aggiorna quando cambia groupingHeader o quando si apre la modale
  useEffect(() => {
    const label = [groupingHeader?.tipo, groupingHeader?.descr]
      .filter(Boolean)
      .join(" — ");
    setValue("tipologiaRaggruppamento", label);
  }, [groupingHeader, setValue]);

  const cols = useMemo(createColumns, []);

  // Salva la modifica di Q.Tà (normalizzata >=0)
  const processRowUpdate = useCallback((newRow: GridRowModel) => {
    const current = newRow as GroupableProcedureRow;
    const safeQta = Number.isFinite(Number(current.qta))
      ? Math.max(0, Number(current.qta))
      : 0;
    const updated: GroupableProcedureRow = { ...current, qta: safeQta };

    setData((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    return updated;
  }, []);

  const onSelectionChange = useCallback((m: GridSelectionModel) => {
    setSelection(m);
  }, []);

  const selectedRows = useMemo(
    () => data.filter((r) => selection.includes(r.id as GridRowId)),
    [data, selection]
  );

  // Costruisce un payload con solo gli slot selezionati (max 10)
  const buildWParaggrPayload = (meta: { aziendaPrenotazione: string }) => {
    const base: Record<string, string> = {
      aziendaPrenotazione: meta.aziendaPrenotazione ?? "",
      idPacada,
    };

    // uso sempre data + selection per evitare stale
    const rows = data
      .filter((r) => selection.includes(r.id as GridRowId))
      .slice(0, 10);

    rows.forEach((row, idx) => {
      const slot = String(idx + 1).padStart(2, "0");
      if (row?.specialita) base[`codSpecia${slot}`] = row.specialita;
      if (row?.prestazione) base[`codAnapre${slot}`] = row.prestazione;
      if (row?.codSiss) base[`codAnapres${slot}`] = row.codSiss;
      if ((row?.qta ?? 0) > 0) base[`quantita${slot}`] = String(row.qta);
    });

    setValue("slotOccupati", String(rows.length));

    return base;
  };

  const handleConfirm = async () => {
    if (!aziendaPrenotazione || !idPacada) {
      console.warn("[GroupableProcedures] meta mancanti", {
        aziendaPrenotazione,
        idPacada,
      });
      onClose();
      return;
    }

    const hasSelection = data.some((r) =>
      selection.includes(r.id as GridRowId)
    );
    if (!hasSelection) {
      console.warn("[GroupableProcedures] nessuna riga selezionata");
      onClose();
      return;
    }

    const payload = buildWParaggrPayload({ aziendaPrenotazione });
    //Invio il payload a calendar
    await onConfirm?.(payload);
  };

  //Alla chiusura del modale resetto le selezioni e le textfield
  const handleClose = useCallback(() => {
    setSelection([]);
    methods.reset({
      tipologiaRaggruppabili: "",
      tipologiaRaggruppamento: "",
      slotOccupati: "",
    });
    onClose();
  }, [onClose, methods]);

  //Quando chiudo il modale svuoto tutti i dati
  useEffect(() => {
    if (!open) {
      setData([]);
      setSelection([]);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={(_, r) => (r === "backdropClick" ? null : handleClose())}
      disableEscapeKeyDown
      keepMounted
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.2)" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(92vw, 980px)",
          maxHeight: "88vh",
          outline: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: `1px solid ${COLORS.border}`,
            bgcolor: COLORS.white,
            overflow: "auto",
            maxHeight: "88vh",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: "Rubik", fontWeight: 500, color: "black", mb: 1 }}
          >
            Prestazioni raggruppabili
          </Typography>

          {/* Header form */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={8}>
              <RHFTextField
                control={methods.control}
                size="small"
                fullWidth
                label="Tipologia raggruppamento"
                name="tipologiaRaggruppamento"
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RHFTextField
                control={methods.control}
                size="small"
                fullWidth
                label="Slot occupati"
                name="slotOccupati"
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={sectionTitleSx}>
            Prestazioni raggruppabili
          </Typography>
          <StyledDataGrid
            editMode="cell"
            rows={data}
            columns={cols}
            checkboxSelection
            selectionModel={selection}
            onSelectionModelChange={onSelectionChange}
            processRowUpdate={processRowUpdate}
            experimentalFeatures={{ newEditingApi: true }}
            disableSelectionOnClick
            sx={{
              height: 200,
              mb: 2.5,
              '& .MuiDataGrid-columnHeaders [data-field="__pen__"] .MuiDataGrid-sortIcon':
                { display: "none" },
            }}
          />

          <Typography variant="subtitle2" sx={sectionTitleSx}>
            Prestazioni selezionate
          </Typography>
          <StyledDataGrid
            disableSelectionOnClick
            rows={selectedRows}
            columns={cols}
            checkboxSelection
            selectionModel={selection}
            onSelectionModelChange={onSelectionChange}
            processRowUpdate={processRowUpdate}
            experimentalFeatures={{ newEditingApi: true }}
            sx={{
              height: 200,
              mb: 2,
              '& .MuiDataGrid-columnHeaders [data-field="__pen__"] .MuiDataGrid-sortIcon':
                { display: "none" },
            }}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Button variant="outlinedError" onClick={onClose}>
                Uscita
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={() => {
                    handleConfirm();
                  }}
                >
                  Conferma
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Modal>
  );
};

export default GroupableProcedures;
